// dm_chat: an open direct-message conversation reads like a chat (the ordinals
// chat's look): messages grouped into runs by sender, a face and a byline once
// per run, no card frames, the newest message at the bottom. Per-user rollout.

import { userHasFeature } from 'refactor/hooks/useUserHasFeature'
import { isDirectMessageRoom } from 'lib/directMessages'

export const DM_CHAT_FEATURE = 'dm_chat'

// A run continues while the same person keeps writing within this window.
export const DM_CHAT_RUN_WINDOW_MS = 5 * 60 * 1000

export type DmChatRun = 'head' | 'cont'

type ChatMessage =
    | {
          id?: string | number | null
          user_id?: string | number | null
          user?: { id?: string | number | null } | null
          created_at?: string | number | Date | null
      }
    | null
    | undefined

export function dmChatEnabled(): boolean {
    return userHasFeature(DM_CHAT_FEATURE)
}

// The conversation itself (not its branches), for someone with the flag.
export function isDmChatQuest(quest: Parameters<typeof isDirectMessageRoom>[0]): boolean {
    return dmChatEnabled() && isDirectMessageRoom(quest)
}

function authorId(message: ChatMessage): string | null {
    const id = message?.user_id ?? message?.user?.id
    return id == null ? null : String(id)
}

function createdAtMs(message: ChatMessage): number | null {
    const value = message?.created_at
    if (value == null) return null
    const numeric = Number(value)
    if (Number.isFinite(numeric)) return numeric
    const parsed = Date.parse(String(value))
    return Number.isFinite(parsed) ? parsed : null
}

function dayKey(message: ChatMessage): string | null {
    const ms = createdAtMs(message)
    if (ms == null) return null
    const date = new Date(ms)
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

// Whether `message` opens a run or continues the one `previous` is in. The
// conversation's head (the letter) is never a message, so pass null for the
// first message.
export function dmChatRunFor(previous: ChatMessage, message: ChatMessage): DmChatRun {
    if (!previous) return 'head'
    const author = authorId(message)
    if (!author || author !== authorId(previous)) return 'head'
    if (dmChatDayLabel(previous, message)) return 'head'
    const gap = (createdAtMs(message) ?? 0) - (createdAtMs(previous) ?? 0)
    return gap >= 0 && gap <= DM_CHAT_RUN_WINDOW_MS ? 'cont' : 'head'
}

// The divider above `message` when its day differs from the previous
// message's ("Today", "Yesterday", or a date); null when the day is the same.
export function dmChatDayLabel(
    previous: ChatMessage,
    message: ChatMessage,
    now: Date = new Date(),
): string | null {
    const day = dayKey(message)
    if (!day || (previous && dayKey(previous) === day)) return null

    const ms = createdAtMs(message) as number
    const date = new Date(ms)
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    const that = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
    const days = Math.round((today - that) / (24 * 60 * 60 * 1000))
    if (days === 0) return 'Today'
    if (days === 1) return 'Yesterday'
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    })
}
